import React from 'react'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import Button from '@mui/material/Button'
import moment from 'moment'

import * as cp from '../../comps'
import * as gs from '../../../app/gStyles'

import { firstElectricContract } from '../../constants'

import ContractLogo from './ContractLogo'
import ReportPages, { horizontalPad } from './ReportPages'
import ContractDetailRow from './ContractDetailRow'
//@ts-ignore
import styles from './SecurityContract.module.css'

export type FirstElectricContractProps = c.r<{
  customerID: string
  isPDF: boolean
  toggleSecuritySignatureModalCompany(): void
  toggleSecuritySignatureModalCustomer(): void
}>

export default React.memo<FirstElectricContractProps>(
  function FirstElectricContract({
    customerID,
    isPDF,
    toggleSecuritySignatureModalCompany,
    toggleSecuritySignatureModalCustomer,
  }) {
    //#region styling
    //#endregion
    //#region global
    const { contractCompanyFull } = r.useContractCompany(customerID)

    const totalCost = r.useTotalCost('customer', customerID)

    const [customerData] = r.useCustomer(customerID)

    const {
      customerAddress,
      customerEmail,
      customerName,
      customerPhone,
      homeRep,
      security_cams,
      security_cams_show,
      security_system,
      security_system_show,
      security_tv,
      security_tv_show,

      security_contract_sign_company,
      security_contract_sign_company_date,
      security_contract_sign_customer,
      security_contract_sign_customer_date,
      solarCompany,
    } = customerData

    const { rows: _secCamRows } = r.useRowable<c.CamGroup>(
      customerID,
      'security_cams_detail',
    )
    const secCamRows =
      security_cams === 'no' || security_cams_show === 0
        ? c.EMPTY_ARRAY
        : _secCamRows.filter((cg) => !!cg.qty && !!cg.type)
    const { rows: _secSysRows } = r.useRowable<c.SecSysGroup>(
      customerID,
      'security_system_detail',
    )
    const secSysRows =
      security_system === 'no' || security_system_show === 0
        ? c.EMPTY_ARRAY
        : _secSysRows.filter((ssg) => !!ssg.qty && !!ssg.type)
    const { rows: _secTVRows } = r.useRowable<c.SecurityTV>(
      customerID,
      'security_tv_detail',
    )
    const secTVRows =
      security_tv === 'no' || security_tv_show === 0
        ? c.EMPTY_ARRAY
        : _secTVRows.filter((ssg) => !!ssg.type)

    const [coBuyer, , coBuyerHandle] = r.useCustomerFieldInput(
      customerID,
      'security_contract_co_buyer',
    )
    //#endregion
    //#region local
    //#endregion

    const buttonSignatureStyle = React.useMemo(
      () => ({
        backgroundColor: c.companyColors[solarCompany],
        '&:hover': { backgroundColor: c.companyColors[solarCompany] },
        width: 256,
      }),
      [solarCompany],
    )

    const activeCustomFields = React.useMemo(
      (): c.CustomerFields =>
        c
          .getFilledCustomFields(customerData)
          .filter(
            (field) => !!customerData[(field + '_show') as c.CustomerField],
          ),
      [customerData],
    )
    const activeCustomFieldsQties = React.useMemo(
      (): c.numbers => getCustomFieldsQties(activeCustomFields, customerData),
      [activeCustomFields, customerData],
    )
    const activeCustomFieldsNames = React.useMemo(
      (): c.strings => getCustomFieldsNames(activeCustomFields, customerData),
      [activeCustomFields, customerData],
    )
    const activeCustomFieldsCosts = React.useMemo(
      (): c.strings => getCustomFieldsCosts(activeCustomFields, customerData),
      [activeCustomFields, customerData],
    )
    const activeCustomFieldsNotes = React.useMemo(
      (): readonly c.strings[] =>
        getCustomFieldsNotes(activeCustomFields, customerData),
      [activeCustomFields, customerData],
    )

    const header = (
      <div>
        <p className={styles['heading']}>{contractCompanyFull.longName}</p>

        <p style={sx.companyInfo}>{contractCompanyFull.address1}</p>
        <p style={sx.companyInfo}>{contractCompanyFull.address2}</p>
        <p style={sx.companyInfo}>{contractCompanyFull.email}</p>
        <p style={sx.companyInfo}>{contractCompanyFull.phoneNumber}</p>
      </div>
    )

    return (
      <>
        <ReportPages companyBorder customerID={customerID} hPad>
          <div style={sx.logo}>
            <ContractLogo isPDF={isPDF} size="l" />
          </div>

          {gapBig}

          {header}

          {gapMedium}

          <p style={sx.node}>
            This is a Purchase Agreement ("PA") on this Date{' '}
            <span className={styles['underlined']}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {security_contract_sign_customer || security_contract_sign_company
                ? moment(
                    security_contract_sign_customer_date ||
                      security_contract_sign_company_date,
                  ).format(c.readableDateFormat)
                : moment().format(c.readableDateFormat)}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>{' '}
            between {contractCompanyFull.longName} ("Company") and:
          </p>

          {gapMedium}

          <p style={sx.node}>
            Buyer:{' '}
            <span className={styles['underlined']}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {customerName}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>{' '}
            Co-Buyer:{' '}
            <input
              className={styles['input-inline-wide']}
              disabled={
                !!security_contract_sign_customer ||
                !!security_contract_sign_company
              }
              onChange={coBuyerHandle}
              title="Co-Buyer"
              value={coBuyer}
            />
          </p>

          <p style={sx.node}>
            Property Address:{' '}
            <span className={styles['underlined']}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {customerAddress}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>
          </p>

          <p style={sx.node}>
            Home / Cell Home:
            <span className={styles['underlined']}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {customerPhone}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>{' '}
            E-mail:
            <span className={styles['underlined']}>
              {c.blankChar(8)}
              {customerEmail}
              {c.blankChar(8)}
            </span>
          </p>

          <p style={sx.node}>
            sometimes collectively referred to herein as the "Parties or
            individually a "Party".
          </p>

          {gapSmall}

          <p style={sx.node}>
            Rep Name:
            <span className={styles['underlined']}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {homeRep}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>{' '}
            Rep Phone:
            <span className={styles['underlined']}>
              {c.blankChar(8)}
              n/a
              {c.blankChar(8)}
            </span>
          </p>

          {gapMedium}

          <p className={styles['subheading']}>SCOPE OF WORK</p>

          {gapMedium}

          <div
            style={{
              flexDirection: 'row',
              display: 'flex',
              width: '100%',
              margin: 0,
            }}
          >
            <p
              style={{ fontWeight: 'bold', textAlign: 'center', width: '15%' }}
            >
              Quantity
            </p>

            <p style={{ fontWeight: 'bold', width: '55%' }}>Description</p>

            <p style={{ fontWeight: 'bold', textAlign: 'end', width: '15%' }}>
              Unit Cost
            </p>

            <p style={{ fontWeight: 'bold', textAlign: 'end', width: '15%' }}>
              Total Cost
            </p>
          </div>

          {secCamRows.map(({ id, notes, price, type, qty }) => (
            <ContractDetailRow
              description={type}
              key={id}
              notes={notes.split('\n')}
              qty={qty}
              price={price}
            />
          ))}

          {secSysRows.map(({ id, notes, price, type, qty }) => (
            <ContractDetailRow
              description={type}
              key={id}
              notes={notes.split('\n')}
              qty={qty}
              price={price}
            />
          ))}

          {secTVRows.map(({ id, price, type }) => (
            <ContractDetailRow
              description={type}
              key={id}
              notes={c.EMPTY_ARRAY}
              qty={1}
              price={price}
            />
          ))}

          {activeCustomFields.map((k, i) => (
            <ContractDetailRow
              description={activeCustomFieldsNames[i]!}
              key={k}
              notes={activeCustomFieldsNotes[i]!}
              price={
                c.parseFloating(activeCustomFieldsCosts[i]!) /
                activeCustomFieldsQties[i]!
              }
              qty={activeCustomFieldsQties[i]!}
            />
          ))}

          {gapSmall}

          <p style={sx.grandTotal}>
            <span style={{ ...sx.bold, fontSize: fontSize * 1.4 }}>
              TOTAL: {c.blankChar(12)}
              {Intl.NumberFormat('en-US', {
                currency: 'USD',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
                style: 'currency',
              }).format(totalCost)}
            </span>
          </p>

          {gapMedium}

          {firstElectricContract.map(({ body, title }, i) => (
            <React.Fragment key={i}>
              <p style={sx.node}>
                <span style={sx.bold}>{title && title + '. '}</span>
                {body}.
              </p>

              {gapSmall}
            </React.Fragment>
          ))}

          {/* This flow-root ensures signatures aren't awkwardly by themselves on
        a lone page, tying them together with the last paragraph of 
        conditions */}
          <div style={gs.flowRootWidth100}>
            <div className={styles['signatures']}>
              <div className={styles['signature-container']}>
                <div>
                  <p className={styles['label-signature']}>Property owner:</p>

                  <span>{customerName}</span>
                </div>

                {gs.rowGap24}

                {security_contract_sign_customer && (
                  <>
                    <img
                      alt=""
                      className={styles['signature-img']}
                      src={security_contract_sign_customer}
                      width={200}
                    />
                    <p>
                      {moment(security_contract_sign_customer_date).format(
                        'YYYY/MM/DD - hh:mma',
                      )}
                    </p>
                  </>
                )}

                <p>Address: {customerAddress}</p>

                {gs.rowGap24}

                {isPDF || security_contract_sign_customer ? (
                  <div />
                ) : (
                  <Button
                    onClick={toggleSecuritySignatureModalCustomer}
                    sx={buttonSignatureStyle}
                    variant="contained"
                  >
                    Client Signature
                  </Button>
                )}
              </div>

              <div className={styles['signature-container']}>
                <div>
                  <p className={styles['label-signature']}>Contractor:</p>
                  <span>{homeRep}</span>
                </div>

                {security_contract_sign_company && (
                  <>
                    <img
                      alt=""
                      className={styles['signature-img']}
                      src={security_contract_sign_company}
                      width={200}
                    />
                    <p>
                      {moment(security_contract_sign_company_date).format(
                        'YYYY/MM/DD - hh:mma',
                      )}
                    </p>
                  </>
                )}

                {gs.rowGap24}

                <p className={styles['label-signature']}>
                  Authorized Representative of
                </p>
                <p className={styles['label-signature']}>
                  {contractCompanyFull.longName}
                </p>

                {gs.rowGap24}

                {isPDF || security_contract_sign_company ? (
                  <div />
                ) : (
                  <Button
                    onClick={toggleSecuritySignatureModalCompany}
                    sx={buttonSignatureStyle}
                    variant="contained"
                  >
                    Authorized Signature
                  </Button>
                )}
              </div>
            </div>
          </div>
        </ReportPages>

        <cp.PageReport company={solarCompany} companyBorder hPad>
          {header}
          {gapMedium}
          <p className={styles['subheading']}>Notice To Buyer</p>
          {gapSmall}
          <p style={sx.node}>
            You are entering into a Purchase Agreement, to purchase certain
            energy efficient improvements
          </p>
          {gapSmall}
          <p style={sx.node}>
            Notice to the Buyer (do not sign this Purchase Agreement before you
            read it & all its attachments or if it contains blank space), you
            will be sent a copy of the agreement you sign. You, the Buyer(s),
            may cancel this Purchase Agreement at any time prior to midnight of
            the third business day after the date of this transaction without a
            penalty.
          </p>
          {gapSmall}
          <p style={sx.node}>
            If the project includes solar a cancellation fee of $1,750.00 will
            be charged to you if you cancel after the 3rd business day. After a
            period of 7 days or more the cancellation fee will increase and
            include: the costs of pictures & measurements / solar engineering
            work completed / any additional engineering for ground-mount site
            visit / permit fees / utility interconnection fees / homeowner's
            association fees / materials shipping & restocking fees / attorney
            fees / lender & financing fees / project management & admin fees /
            engineering stamp fees / any other fees the Company accrued.
          </p>
          {gapSmall}
          <p style={sx.node}>
            Under the right to rescind, you have until midnight of the third
            business day to cancel the Purchase Agreement. Day one begins after
            this occurs:
          </p>
          {gapSmall}
          <p style={sx.node}>- YOU SIGN THE PURCHASE AGREEMENT</p>
          {gapSmall}
          <p style={sx.node}>
            - YOU RECEIVE A TRUTH IN LENDING DISCLOSURE FORM CONTAINING CERTAIN
            KEY INFORMATION ABOUT YOUR CREDIT CONTRACT, INCLUDING THE ANNUAL
            PERCENTAGE RATE; AMOUNT FINANCED AND PAYMENT SCHEDULE.
          </p>
          {gapSmall}
          <p style={sx.node}>
            If you decide to rescind, you must notify{' '}
            {contractCompanyFull.longName} in writing. You may not rescind by
            telephone or by a face-to-face conversation with your sales
            representative. Your written notice must be mailed, or delivered by
            other written means, before midnight of the third business day to
            the Company's Corporate Office{' '}
            {contractCompanyFull.address1 + ' ' + contractCompanyFull.address2}.
          </p>
          {gapSmall}
          <p className={styles['subheading']}>NOTICE OF CANCELLATION</p>
          {gapSmall}
          <p style={sx.bold}>
            DATE OF TRANSACTION:{' '}
            <span className={styles['underlined']}>{c.blankChar(64)}</span>
          </p>
          {gapSmall}
          <p style={sx.node}>
            YOU MAY CANCEL THIS TRANSACTION, WITHOUT ANY PENALTY OR OBLIGATION,
            WITHIN THREE BUSINESS DAYS FROM THE ABOVE DATE. IF YOU CANCEL:
          </p>
          <p style={sx.node}>
            1. ANY PROPERTY TRADED IN, ANY PAYMENTS MADE BY YOU UNDER THE
            CONTRACT OR SALE, AND ANY NEGOTIABLE INSTRUMENT EXECUTED BY YOU WILL
            BE RETURNED WITHIN 10 BUSINESS DAYS FOLLOWING RECEIPT BY THE COMPANY
            OF YOUR CANCELLATION NOTICE, AND ANY SECURITY INTEREST ARISING OUT
            OF THE TRANSACTION WILL BE CANCELED.
          </p>
          <p style={sx.node}>
            2. YOU MUST MAKE AVAILABLE TO THE COMPANY AT YOUR PROPERTY, IN
            SUBSTANTIALLY AS GOOD CONDITION AS WHEN RECEIVED, ANY GOODS
            DELIVERED TO YOU UNDER THIS PURCHASE AGREEMENT OR SALE; OR YOU MAY
            IF YOU WISH, COMPLY WITH THE INSTRUCTIONS OF THE COMPANY REGARDING
            THE RETURN SHIPMENT OF THE PRODUCTS AT THE COMPANY'S EXPENSE AND
            RISK.
          </p>
          <p style={sx.node}>
            3. IF THE COMPANY DOES NOT PICK UP THE DELIVERED GOODS UNDER THIS
            PURCHASE AGREEMENT WITHIN 10 DAYS OF THE DATE OF YOUR NOTICE OF
            CANCELLATION, YOU MAY RETAIN OR DISPOSE OF THE GOODS WITHOUT ANY
            FURTHER OBLIGATION.
          </p>
          <p style={sx.node}>
            4. TO CANCEL THIS TRANSACTION, MAIL OR DELIVER A SIGNED AND DATED
            COPY OF THIS CANCELLATION NOTICE OR ANY OTHER WRITTEN NOTICE, TO{' '}
            {contractCompanyFull.longName}{' '}
            {contractCompanyFull.address1 + ' ' + contractCompanyFull.address2}.
          </p>
          {gapSmall}
          <p style={sx.node}>
            NOT LATER THAN MIDNIGHT OF THIS DATE:{' '}
            <span className={styles['underlined']}>{c.blankChar(128)}</span>
          </p>
          {gapSmall}
          <p style={sx.node}>I HEREBY CANCEL THIS PURCHASE AGREEMENT</p>
          {gapSmall}
          <p style={sx.bold}>
            Buyers Signatures:{' '}
            <span className={styles['underlined']}>{c.blankChar(48)}</span>
            Buyers Names:{' '}
            <span className={styles['underlined']}>{c.blankChar(48)}</span>
          </p>
          {gapSmall}
          <p style={sx.bold}>
            Date:{' '}
            <span className={styles['underlined']}>{c.blankChar(48)}</span>
          </p>
        </cp.PageReport>
      </>
    )
  },
)

const gapBig = gs.rowGap48
const gapMedium = gs.rowGap32
const gapSmall = gs.rowGap16

const fontSize = 11

const sxNode = {
  alignSelf: 'flex-start',
  fontFamily: c.light.fontFamily,
  fontSize,
  margin: 0,
}
const sx = {
  bold: { ...sxNode, fontWeight: 'bold' },
  boldCursive: { ...sxNode, fontStyle: 'italic', fontWeight: 'bold' },
  companyInfo: {
    fontFamily: c.light.fontFamily,
    margin: 0,
    textAlign: 'center' as const,
  },
  grandTotal: { ...sxNode, textAlign: 'end' as const, width: '100%' },
  node: sxNode,
  logo: { left: horizontalPad, position: 'absolute' as const, top: '96px' },
}

export const getCustomFieldsQties = (
  activeCustomFields: c.CustomerFields,
  customerData: c.Customer,
): c.numbers =>
  activeCustomFields.map(
    (field): number =>
      customerData[(field + '_qty') as c.CustomerField] as number,
  )

export const getCustomFieldsNames = (
  activeCustomFields: c.CustomerFields,
  customerData: c.Customer,
): c.strings =>
  activeCustomFields.map(
    (field): string =>
      customerData[(field + '_name') as c.CustomerField] as string,
  )

export const getCustomFieldsCosts = (
  activeCustomFields: c.CustomerFields,
  customerData: c.Customer,
): c.strings =>
  activeCustomFields.map(
    (field): string =>
      customerData[(field + '_customer_cost') as c.CustomerField] as string,
  )

export const getCustomFieldsNotes = (
  activeCustomFields: c.CustomerFields,
  customerData: c.Customer,
) =>
  activeCustomFields.map((field): c.strings => {
    const notes = customerData[(field + '_notes') as c.CustomerField] as string

    return notes.split('\n').filter((_) => !!_)
  })
